import type { ReactNode } from "react";

import { cn } from "@/utils/cn";
import { USAGE_RING_CLASS, type UsageStatus } from "@/utils/subscription.util";

interface PlanUsageRingProps {
  /** Arc fill, 0–100. */
  percentage: number;
  status: UsageStatus;
  /** Outer square size in px. */
  size?: number;
  strokeWidth?: number;
  /** Centered content (usually the resource icon). */
  children?: ReactNode;
  /** Track color — override when the ring sits on a tinted surface. */
  trackClassName?: string;
  className?: string;
}

/** Circular progress arc with an optional centered glyph. */
export const PlanUsageRing = ({
  percentage,
  status,
  size = 28,
  strokeWidth = 2.5,
  children,
  trackClassName = "stroke-muted",
  className,
}: PlanUsageRingProps) => {
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const fill = (Math.min(Math.max(percentage, 0), 100) / 100) * circumference;

  return (
    <div
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        aria-hidden
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={trackClassName}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${fill} ${circumference}`}
          className={cn(
            "transition-[stroke-dasharray] duration-500 ease-out",
            USAGE_RING_CLASS[status],
          )}
        />
      </svg>
      {children && (
        <span className="absolute inset-0 flex items-center justify-center">
          {children}
        </span>
      )}
    </div>
  );
};
